const round2 = (n) => Math.round(Number(n || 0) * 100) / 100

export const sumaConceptos = (conceptos = []) =>
  round2((conceptos || []).reduce((s, c) => s + Number(c.monto || 0), 0))

export const sumaParcialidades = (parcialidades = []) =>
  round2((parcialidades || []).reduce((s, p) => s + Number(p.monto || 0), 0))

// Total que REALMENTE se guarda y aplica: solo conceptos con concepto seleccionado,
// una linea por concepto y los repetidos SUMADOS en esa linea.
//
// Sumar y no descartar es lo unico que mantiene al cliente de acuerdo con el
// servidor: asignarPlan valida el cuadre sumando TODOS los items del plan. Descartar
// repetidos (como se hacia) rompia los planes que legitimamente llevan el mismo
// concepto varias veces -- p.ej. 4 lineas de "Mensualidad" de $500: el editor veia
// $500 y el servidor $2,000, asi que el plan jamas podia cuadrar y no habia forma de
// arreglarlo desde la pantalla. Con la suma, un duplicado accidental tampoco se
// esconde: aparece como una linea con el monto inflado, a la vista y corregible.
export const conceptosGuardables = (conceptos = []) => {
  const porConcepto = new Map()
  for (const c of (conceptos || [])) {
    if (!c.concepto_id) continue
    const prev = porConcepto.get(c.concepto_id)
    if (prev) prev.monto = round2(Number(prev.monto || 0) + Number(c.monto || 0))
    else porConcepto.set(c.concepto_id, { ...c, monto: round2(Number(c.monto || 0)) })
  }
  return [...porConcepto.values()]
}

export const totalConceptosGuardables = (conceptos = []) =>
  sumaConceptos(conceptosGuardables(conceptos))

export const validarParcialidades = (conceptos = [], parcialidades = []) => {
  const total = sumaConceptos(conceptos)
  const sumaParc = sumaParcialidades(parcialidades)
  const diferencia = round2(sumaParc - total)
  return { total, sumaParc, diferencia, cuadra: diferencia === 0 }
}

const addMonths = (isoDate, months) => {
  const d = new Date(isoDate + 'T12:00:00')
  d.setMonth(d.getMonth() + months)
  return d.toISOString().split('T')[0]
}

// Genera el CALENDARIO de pagos (parcialidades) con etiquetas neutras "Pago N".
// Las parcialidades son solo el reparto del total en el tiempo: NO son conceptos.
// `primeraMonto` = un primer pago distinto (ej. el enganche/inscripción), si se quiere.
// `redondeo` = unidad para redondear los pagos (0 = exacto al centavo; 1 = sin
// centavos; 50/100 = cifras limpias). Con redondeo, los pagos iguales se redondean
// a esa unidad y el ÚLTIMO pago absorbe el resto, así siempre cuadra el total.
export const generarParcialidades = ({ total, numParcialidades, fechaInicio, primeraMonto = 0, redondeo = 0 }) => {
  const n = Number(numParcialidades) || 0
  const primera = Number(primeraMonto) || 0
  const unidad = Number(redondeo) || 0
  const out = []
  let orden = 1

  if (primera > 0) {
    out.push({ orden, nombre: `Pago ${orden}`, monto: round2(primera), fecha_limite: fechaInicio || null })
    orden++
  }

  const restante = round2(Number(total || 0) - primera)
  if (n > 0) {
    const exacta = restante / n
    let base = unidad > 0 ? Math.round(exacta / unidad) * unidad : round2(exacta)
    if (!(base > 0)) base = round2(exacta) // unidad demasiado grande -> evita pagos en $0
    let acumulado = 0
    for (let i = 0; i < n; i++) {
      const esUltima = i === n - 1
      const monto = esUltima ? round2(restante - acumulado) : round2(base)
      acumulado = round2(acumulado + monto)
      const offset = i + (primera > 0 ? 1 : 0)
      out.push({
        orden,
        nombre: `Pago ${orden}`,
        monto,
        fecha_limite: fechaInicio ? addMonths(fechaInicio, offset) : null,
      })
      orden++
    }
  }
  return out
}

// Que le pasaria a los cargos ya emitidos si se aplicaran los montos VIGENTES de
// las parcialidades. Puro y compartido: el recalculo lo ejecuta y la pantalla de
// Planes lo usa (en seco) para marcar los planes con cambios sin aplicar, asi la
// marca y el efecto real nunca pueden discrepar.
//
// mode 'total' = todos los cargos; 'pendiente' = solo los que no tienen ni un peso
// pagado. Lo COBRADO (monto_pagado) jamas se toca: si el ajuste deja el cargo por
// debajo de lo pagado, la diferencia sale como saldo a favor.
export const calcularAjustesCargos = (parcMonto = {}, cargos = [], mode = 'total') => {
  const cambios = []
  for (const c of (cargos || [])) {
    const nuevoOriginal = parcMonto[c.parcialidad_id]
    if (c.parcialidad_id == null || nuevoOriginal == null) continue
    const pagado = Number(c.monto_pagado || 0)
    if (mode === 'pendiente' && pagado > 0) continue
    const desc = Number(c.descuento_porcentaje || 0)
    const descMonto = round2(nuevoOriginal * (desc / 100))
    const nuevoFinal = round2(nuevoOriginal - descMonto)
    if (round2(nuevoFinal - Number(c.monto_final || 0)) === 0) continue
    let nuevoSaldo = round2(nuevoFinal - pagado)
    let sobrante = 0
    if (nuevoSaldo < 0) { sobrante = round2(-nuevoSaldo); nuevoSaldo = 0 }
    let estatus = 'pendiente'
    if (nuevoSaldo <= 0) estatus = 'pagado'
    else if (pagado > 0) estatus = 'parcial'
    cambios.push({
      id: c.id, plan_id: c.plan_id, jugador_id: c.jugador_id, temporada_id: c.temporada_id,
      nombre: c.jugadores?.nombre_completo || 'Jugador',
      antes: Number(c.monto_final || 0), despues: nuevoFinal,
      nuevoOriginal, descMonto, nuevoSaldo, estatus, sobrante,
    })
  }
  return cambios
}

// Cargos que NUNCA se emitieron: cada (jugador asignado x parcialidad) sin cargo.
// Aparecen cuando se le agrega un pago a un plan que ya cobra -- el pago nuevo no
// le llega a quien ya estaba asignado.
//
// Un cargo CANCELADO cuenta como existente: cancelarlo fue una decision del club y
// completar el plan no debe deshacerla. Se devuelven aparte para poder decirlo.
export const calcularCargosFaltantes = ({ asignados = [], parcialidades = [], cargos = [] }) => {
  const vivos = new Set()
  const cancelados = new Set()
  ;(cargos || []).forEach((c) => {
    const k = `${c.jugador_id}|${c.parcialidad_id}`
    if (c.estatus === 'cancelado') cancelados.add(k)
    else vivos.add(k)
  })

  const faltantes = []
  let omitidosPorCancelacion = 0
  for (const a of (asignados || [])) {
    for (const p of (parcialidades || [])) {
      const k = `${a.jugador_id}|${p.id}`
      if (vivos.has(k)) continue
      if (cancelados.has(k)) { omitidosPorCancelacion++; continue }
      faltantes.push({
        jugador_id: a.jugador_id,
        nombre: a.nombre || 'Jugador',
        parcialidad_id: p.id,
        nombreParcialidad: p.nombre || 'Pago',
        monto: Number(p.monto || 0),
      })
    }
  }
  return { faltantes, omitidosPorCancelacion }
}

// ¿Este plan se puede asignar? Misma condicion que valida asignarPlan en el
// servidor: tiene calendario de pagos y ese calendario cuadra con su total.
// Ofrecer un plan a medias lleva a elegir jugadores y chocar con un error hasta
// el final, cuando ya se invirtio el trabajo de seleccionarlos.
export const planListoParaAsignar = (plan) => {
  const parcs = plan?.pagos_planes_parcialidades || []
  if (!parcs.length) return false
  const total = round2((plan?.pagos_planes_items || []).reduce((s, i) => s + Number(i.monto || 0), 0))
  if (!(total > 0)) return false  // un plan en $0 no cobra nada: no hay que asignar
  return round2(sumaParcialidades(parcs) - total) === 0
}
